import React, { useEffect, useState, useRef } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  BiMenu,
  BiUserCircle,
  BiLogOut,
  BiTachometer,
  BiBriefcase,
  BiGroup,
  BiTask,
  BiTimeFive,
  BiMap,
  BiDotsVertical,
  BiBuilding,
  BiStar,
  BiCalendar,
  BiChevronDown,
  BiDollarCircle,
  BiChevronUp,
  BiArchive,
  BiVideo,
  BiRocket,
  BiChevronRight,
} from "react-icons/bi";
import axiosClient from "../utils/apiClient.js";
import GlobalTour from "../Onboarding/GlobalTour";
import AdminWelcomeModal from "../Onboarding/AdminWelcomeModal";
import { useOnboarding } from "../Onboarding/OnboardingContext";

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState("");
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const sidebarRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { runTour, stepIndex, setStepIndex } = useOnboarding();

  const user = JSON.parse(localStorage.getItem("user"));
  const organizationName = user?.organization?.name || "N/A";
  const roleName = user?.roleName || "guest";
  const planType = user?.organization?.planType || "N/A";
  const userLimit = user?.organization?.userLimit;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("stripeAccountId");
    navigate("/login");
  };

  const formatRoleName = (role = "") =>
    role
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarOpen &&
        !isLargeScreen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setSidebarOpen(false);
      }
      if (
        userMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarOpen, isLargeScreen, userMenuOpen]);

  const toggleSubMenu = (menuName) =>
    setOpenSubMenu(openSubMenu === menuName ? "" : menuName);

  const isLinkOrSubmenuActive = (link) => {
    const currentPath = location.pathname;
    if (currentPath === link.path) return true;
    if (currentPath.startsWith(link.path) && link.path !== "/") return true;
    if (link.submenus)
      return link.submenus.some((submenu) => currentPath === submenu.path);
    return false;
  };

  const getSidebarLinks = () => {
    const caseSubmenus = [
      {
        name: "Process Service",
        path: "/cases",
        tourClass: "tour-sidebar-process",
      },
      { name: "Private Investigation", path: "/cases/pi" },
    ];

    const clientCaseSubmenus = [
      {
        name: "Process Service",
        path: "/client/cases",
        tourClass: "tour-sidebar-process",
      },
      { name: "Private Investigation", path: "/client/cases/pi" },
    ];

    const commonLinks = [
      {
        name: "Dashboard",
        path: `/dashboard/${roleName.replace("_", "-")}`,
        icon: <BiTachometer className="w-5 h-5" />,
        tourClass: "dashboard-tour-start",
      },
    ];

    switch (roleName) {
      case "super_admin":
        return [
          {
            name: "Dashboard",
            path: `/dashboard/${roleName}`,
            icon: <BiTachometer className="w-5 h-5" />,
          },
          {
            name: "Organizations",
            path: "/organizations",
            icon: <BiBuilding className="w-5 h-5" />,
          },
          {
            name: "Transactions",
            path: "/super-admin/transactions",
            icon: <BiDollarCircle className="w-5 h-5" />,
          },
          {
            name: "All Cases",
            path: "/super-admin/cases",
            icon: <BiBriefcase className="w-5 h-5" />,
          },
          {
            name: "All Users",
            path: "/super-admin/users",
            icon: <BiGroup className="w-5 h-5" />,
          },
          {
            name: "Audit Logs",
            path: "/audit-logs",
            icon: <BiTask className="w-5 h-5" />,
          },
          {
            name: "Walkthrough Video",
            path: "/super-admin/quick-start-video",
            icon: <BiVideo className="w-5 h-5" />,
          },
          {
            name: "Onboarding Monitor",
            path: "/super-admin/onboarding-monitor",
            icon: <BiArchive className="w-5 h-5" />,
          },
          {
            name: "Profile",
            path: `/super_admin/${user.id}`,
            icon: <BiUserCircle className="w-5 h-5" />,
          },
        ];

      case "admin": {
        const adminLinks = [
          ...commonLinks,
          {
            name: "Clients",
            path: "/clients",
            icon: <BiUserCircle className="w-5 h-5" />,
            tourClass: "sidebar-clients-link",
          },
          {
            name: "Cases",
            path: "/cases",
            icon: <BiBriefcase className="w-5 h-5" />,
            submenus: caseSubmenus,
            tourClass: "sidebar-cases-link",
          },
          {
            name: "Time & Expenses",
            path: "/time-entries",
            icon: <BiTimeFive className="w-5 h-5" />,
            tourClass: "sidebar-time-expenses-link",
          },
          {
            name: "Invoices",
            path: "/invoices",
            icon: <BiDollarCircle className="w-5 h-5" />,
            tourClass: "sidebar-invoices-link",
          },
          {
            name: "Subscription",
            path: "/subscriptions/manage",
            icon: <BiRocket className="w-5 h-5" />,
          },
        ];
        if (
          userLimit === null ||
          (typeof userLimit === "number" && userLimit > 0)
        ) {
          adminLinks.splice(3, 0, {
            name: "Teams",
            path: "/teams",
            icon: <BiGroup className="w-5 h-5" />,
          });
        }
        return adminLinks;
      }

      case "case_manager":
        return [
          ...commonLinks,
          {
            name: "Clients",
            path: "/clients",
            icon: <BiUserCircle className="w-5 h-5" />,
            tourClass: "sidebar-clients-link",
          },
          {
            name: "Cases",
            path: "/cases",
            icon: <BiBriefcase className="w-5 h-5" />,
            submenus: caseSubmenus,
            tourClass: "sidebar-cases-link",
          },
          {
            name: "Time & Expenses",
            path: "/time-entries",
            icon: <BiTimeFive className="w-5 h-5" />,
            tourClass: "sidebar-time-expenses-link",
          },
          {
            name: "Invoices",
            path: "/invoices",
            icon: <BiDollarCircle className="w-5 h-5" />,
            tourClass: "sidebar-invoices-link",
          },
        ];

      case "field_agent":
        return [
          ...commonLinks,
          {
            name: "Cases",
            path: "/agent/cases",
            icon: <BiBriefcase className="w-5 h-5" />,
          },
          {
            name: "Time & Expenses",
            path: "/agent/time-entries",
            icon: <BiTimeFive className="w-5 h-5" />,
          },
        ];

      case "servers":
        return [
          {
            name: "Dashboard",
            path: "/dashboard/server",
            icon: <BiTachometer className="w-5 h-5" />,
          },
          {
            name: "Cases",
            path: "/server/cases",
            icon: <BiBriefcase className="w-5 h-5" />,
          },
          {
            name: "Time & Expenses",
            path: "server/time-entries",
            icon: <BiTimeFive className="w-5 h-5" />,
          },
        ];

      case "client":
        return [
          {
            name: "Dashboard",
            path: `/dashboard/${roleName}`,
            icon: <BiTachometer className="w-5 h-5" />,
          },
          {
            name: "My Cases",
            path: "/client/cases",
            icon: <BiBriefcase className="w-5 h-5" />,
            submenus: clientCaseSubmenus,
          },
          {
            name: "My Invoices",
            path: `/invoices/client/${user.clientId}`,
            icon: <BiTimeFive className="w-5 h-5" />,
          },
          {
            name: "My Profile",
            path: `/users/${user.id}`,
            icon: <BiUserCircle className="w-5 h-5" />,
          },
        ];

      default:
        return [];
    }
  };

  const sidebarLinks = getSidebarLinks();

  useEffect(() => {
    sidebarLinks.forEach((link) => {
      if (
        link.submenus &&
        link.submenus.some((s) => location.pathname === s.path)
      ) {
        setOpenSubMenu(link.name);
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    if (runTour && stepIndex === 0) setOpenSubMenu("Cases");
  }, [runTour, stepIndex]);

  const handleSidebarLinkClick = (tourClass) => {
    if (!isLargeScreen) setSidebarOpen(false);
    if (runTour && stepIndex === 0 && tourClass === "tour-sidebar-process") {
      setStepIndex(1);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC] text-slate-900 font-sans">
      <GlobalTour />

      {/* Mobile Overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 ${runTour ? 'z-[50]' : 'z-50'} w-72 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out md:sticky md:top-0 md:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ height: "100vh" }}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-slate-100 bg-white sticky top-0 z-10">
            <Link to="/" className="flex items-center gap-2 group">
              {/* <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform">
                <BiRocket className="w-5 h-5" />
              </div> */}
              <span className="text-2xl text-blue-600 font-bold tracking-tight">
                IntelServe
              </span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
            >
              <BiMenu className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1.5 scrollbar-thin scrollbar-thumb-slate-200">
            {sidebarLinks.map((link) => {
              const isActive = isLinkOrSubmenuActive(link);
              const isSubMenuOpen = openSubMenu === link.name;

              if (link.submenus) {
                return (
                  <div key={link.name} className="space-y-1">
                    <button
                      onClick={() => toggleSubMenu(link.name)}
                      className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? "bg-blue-50/50 text-blue-600 font-semibold"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      } ${link.tourClass || ""}`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`transition-colors ${isActive ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"}`}
                        >
                          {link.icon}
                        </span>
                        <span className="text-sm font-medium">{link.name}</span>
                      </div>
                      <BiChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${isSubMenuOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${isSubMenuOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}
                    >
                      <div className="ml-4 pl-6 border-l border-slate-100 space-y-1 my-1">
                        {link.submenus.map((submenu) => {
                          let isSubMenuActive =false;
                          // submenu.path === "/cases"
                          //   ? location.pathname === "/cases"
                          //   : location.pathname === submenu.path ||
                          //     location.pathname.startsWith(submenu.path + "/");
                          if (submenu.path === "/cases") {
                            // Highlight for /cases, /cases/new, /cases/edit/... but EXCLUDE /cases/pi
                            isSubMenuActive = 
                              location.pathname === "/cases" || 
                              (location.pathname.startsWith("/cases/") && !location.pathname.startsWith("/cases/pi"));
                          } 
                          else if (submenu.path === "/client/cases") {
                            // Highlight for client cases, but EXCLUDE client PI cases
                            isSubMenuActive = 
                              location.pathname === "/client/cases" || 
                              (location.pathname.startsWith("/client/cases/") && !location.pathname.startsWith("/client/cases/pi"));
                          } 
                          else {
                            // Standard fallback for everything else (like /cases/pi/new)
                            isSubMenuActive = 
                              location.pathname === submenu.path || 
                              location.pathname.startsWith(submenu.path + "/");
                          }
                          return (
                            <Link
                              key={submenu.name}
                              to={submenu.path}
                              onClick={() => handleSidebarLinkClick(submenu.tourClass)}
                              className={`flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                isSubMenuActive
                                  ? "text-blue-600 font-semibold bg-blue-50/30"
                                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                              } ${submenu.tourClass || ""}`}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className={`w-1.5 h-1.5 rounded-full transition-all ${isSubMenuActive ? "bg-blue-500 scale-125" : "bg-slate-200 group-hover:bg-slate-400"}`}
                                />
                                {submenu.name}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => handleSidebarLinkClick(link.tourClass)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    isActive
                      ? "bg-blue-600 text-white font-semibold shadow-md shadow-blue-200"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  } ${link.tourClass || ""}`}
                >
                  <span
                    className={`transition-colors ${isActive ? "text-white" : "text-slate-400 group-hover:text-slate-600"}`}
                  >
                    {link.icon}
                  </span>
                  <span className="text-sm font-medium">{link.name}</span>
                  {isActive && (
                    <BiChevronRight className="ml-auto w-4 h-4 opacity-50" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Organization Info */}
          {user?.organizationId && (
            <div className="mx-4 mb-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                Organization
              </h3>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 bg-white rounded-lg border border-slate-200 shadow-sm">
                    <BiBuilding className="w-4 h-4 text-slate-500" />
                  </div>
                  <span className="text-xs font-semibold text-slate-700 truncate">
                    {organizationName}
                  </span>
                </div>
                {roleName === "admin" && (
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-blue-50 rounded-lg border border-blue-100">
                      <BiStar className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-[11px] font-bold text-blue-700 uppercase tracking-tight">
                      {planType} Plan
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User Profile */}
          <div className="mt-auto p-4 border-t border-slate-100 bg-white sticky bottom-0">
            {user && (
              <div ref={userMenuRef} className="relative">
                <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors duration-200 border border-transparent hover:border-slate-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center text-white shadow-md">
                    {user.firstName?.charAt(0)}
                    {user.lastName?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      {formatRoleName(user.roleName)}
                    </div>
                  </div>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors text-slate-400 hover:text-slate-600"
                  >
                    <BiDotsVertical className="w-5 h-5" />
                  </button>
                </div>

                {userMenuOpen && (
                  <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-2xl shadow-xl border border-slate-100 z-[60] overflow-hidden p-1.5 animate-in slide-in-from-bottom-2 duration-200">
                    {roleName !== "super_admin" && (
                      <Link
                        to={`/users/${user?.id}`}
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
                      >
                        <BiUserCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">My Profile</span>
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 w-full text-left"
                    >
                      <BiLogOut className="w-5 h-5" />
                      <span className="text-sm font-medium">Logout</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="h-16 bg-white border-b border-slate-200 px-4 md:hidden flex justify-between items-center sticky top-0 z-30 shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
          >
            <BiMenu className="w-7 h-7" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded flex items-center justify-center text-white text-xs">
              <BiRocket />
            </div>
            <span className="text-lg font-bold text-slate-800">IntelServe</span>
          </div>
          <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200" />
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          <div className="max-w-[1600px] mx-auto p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
